import {
  isOutsideImage,
  isOverlapping,
} from '../../components/builders/simulator/utils'
import { UGC_ACCEPTED_STATUS, UGC_REJECTED_STATUS } from '../constants/assets'
import { SEARCH_FOLDER, UGC_FOLDER } from '../constants/folder'
import utils from './DragDropUtils'
import { canMovePublishedAssets, getDraggedAssets } from './publishedMove'
import {
  assetIsReferenced,
  buildPath,
  folderContainsReferencedAssets,
  folderContainsReferencedFolders,
  getEndpointOfNode,
  isChild,
  scrollElement,
  throttle,
  validatePrivilege,
} from './utils'

export const DND_ASSET = 'DND_ASSET'
export const DND_CAROUSEL_ITEM = 'DND_CAROUSEL_ITEM'
export const DND_MENU_ITEM = 'DND_MENU_ITEM'
export const DND_FOLDER_LIB = 'DND_FOLDER_LIB'
export const DND_FOLDER_MST = 'DND_FOLDER_MST'
export const DND_SLIDE = 'DND_SLIDE'
export const DND_HOTSPOT = 'DND_HOTSPOT'
export const DND_NAVZONE = 'DND_NAVZONE'
export const DND_GRID_ITEM = 'DND_GRID_ITEM'

export const assetSource = {
  beginDrag(props) {
    if (props.type === 'folder') {
      // Make sure dragged folder is active!
      if (props.id !== props.folders.activeFolder)
        props.folderActions.activeFolder(props.id)
    }
    if (props.type === 'asset') {
      // Make sure dragged asset is active! Library only — in publishing
      // endpoints drags are reorder-only and selection is status-gated
      if (
        props.selectedAssets.indexOf(props.id) === -1 &&
        getEndpointOfNode(
          props.allFolders[props.activeFolder],
          props.allFolders,
          props.endpoints.endpoints,
        ).id === 'LIB'
      ) {
        props.appActions.selectAsset(props.id)
      }
    }
    return {
      id: props.id,
      type: props.type,
    }
  },

  /*
   * Do not allow dragging assets from search folder
   * In the library, dragging requires the move asset privilege
   * In other endpoints a drag is either a grid reorder (follows canReorder,
   * which already carries the updatefolder privilege check) or a move to
   * another published folder (needs unpublishasset here; the drop target
   * checks the rest - see canMovePublishedAssets)
   */
  canDrag(props) {
    if (props.type !== 'asset') return true
    if (props.activeFolder === SEARCH_FOLDER) return false
    if (
      getEndpointOfNode(
        props.allFolders[props.activeFolder],
        props.allFolders,
        props.endpoints.endpoints,
      ).id !== 'LIB'
    ) {
      return (
        !!props.canReorder ||
        validatePrivilege(
          'unpublishasset',
          props.privileges,
          props.userEmail,
          props.groups,
          props.activeFolder,
          props.allFolders,
        )
      )
    }
    return validatePrivilege(
      'moveasset',
      props.privileges,
      props.userEmail,
      props.groups,
      props.activeFolder,
      props.allFolders,
    )
  },
}

/******************
 * Folder Drag/Drop
 ******************/
export const folderSource = {
  beginDrag(props) {
    props.onBeginDrag(props.id)

    return {
      id: props.id,
      type: props.type,
    }
  },

  endDrag(props) {
    props.onEndDrag(props.id)
  },

  canDrag(props) {
    return utils.folderCanBeDragged(props)
  },
}

export const folderTarget = {
  drop(props, monitor) {
    const droppedItem = monitor.getItem()
    // If we're dropping in the recycle bin...open the delete modal
    // Otherwise, call dropFolder to move the folder
    if (droppedItem.type === 'folder') {
      if (utils.targetIsRecycleBin(props)) {
        props.modalActions.openModal('DeleteModal', {
          type: 'folder',
          folderDragData: props.folderDragData,
        })
      } else {
        const destinationFolder = props.folderDragData.over
          ? props.folderDragData.over
          : props.folderDragData.above
            ? props.folders.byId[props.folderDragData.above].parentId
            : props.folderDragData.below
              ? props.folders.byId[props.folderDragData.below].parentId
              : null
        if (destinationFolder) {
          /*
           * The source parent is the folder that actually lists this id in the
           * tree being dragged in — not necessarily the stored `parentId`.
           * A consumed shared-endpoint folder hangs in BOTH the Library and
           * Published trees under one id, so its `parentId` names only the
           * primary (Library) host. Deriving the parent from it made a
           * reorder inside Published look like a move to a different parent.
           */
          const destination = props.folders.byId[destinationFolder]
          const contains =
            destination &&
            (destination.childFolders || []).indexOf(droppedItem.id) > -1
          const sourceParent = contains
            ? destinationFolder
            : props.folders.byId[droppedItem.id].parentId

          props.folderActions.dropFolder(
            destinationFolder,
            droppedItem.id,
            sourceParent,
            props.folderDragData,
          )
        }
      }
    } else {
      // Dropping an asset - move ALL selected assets
      if (props.folders.activeFolder === UGC_FOLDER) {
        if (utils.targetIsRecycleBin(props))
          props.assetActions.reviewUgcAssets(
            props.selectedAssets,
            UGC_REJECTED_STATUS,
            props.id,
          )
        else
          props.assetActions.reviewUgcAssets(
            props.selectedAssets,
            UGC_ACCEPTED_STATUS,
            props.id,
          )
      } else if (
        getEndpointOfNode(
          props.folders.byId[props.folders.activeFolder],
          props.folders.byId,
          props.endpoints.endpoints,
        ).id !== 'LIB'
      ) {
        // Between two folders of a publishing endpoint: re-publish rather
        // than move the asset record (see publishedMove.js)
        props.endpointActions.movePublishedAssets(
          getDraggedAssets(droppedItem.id, props.selectedAssets),
          props.folders.activeFolder,
          props.id,
        )
      } else {
        props.assetActions.dropAsset(
          props.id,
          props.selectedAssets,
          props.folders.activeFolder,
        )
      }
    }
  },

  // This function tells us whether the object being dragged is allowed to be dropped a given
  // folder
  // Since folders are both a dragItem and dropTarget,
  // only allow drop if we are over a different folder
  // and that folder is not the current parent
  // and that folder is not the search folder
  // and that folder is not a child of the dragged folder
  // and check permissions for move/delete folder
  //
  // Assets -> Only allow drop asset if destination folder is different, and user has move asset
  // privilege there
  canDrop(props, monitor) {
    if (monitor.isOver({ shallow: true })) {
      const dropCandidate = monitor.getItem()
      if (dropCandidate.type === 'asset') {
        // A drag that began in a publishing endpoint may land on another
        // folder of the same endpoint: that is a published move, not a
        // Library move, and has its own rules
        if (
          getEndpointOfNode(
            props.folders.byId[props.folders.activeFolder],
            props.folders.byId,
            props.endpoints.endpoints,
          ).id !== 'LIB'
        ) {
          return canMovePublishedAssets({
            assets: getDraggedAssets(dropCandidate.id, props.selectedAssets),
            sourceFolder: props.folders.activeFolder,
            destFolder: props.id,
            allFolders: props.folders.byId,
            endpoints: props.endpoints,
            folderAssetStatus: props.endpoints.folderAssetStatus,
            privileges: props.privileges,
            email: props.email,
            groups: props.groups,
            recycleBin: props.folders.recycleBin,
          })
        }
        // Can't drop published assets or referenced assets in recycleBin
        if (utils.targetIsRecycleBin(props)) {
          if (
            assetIsReferenced(dropCandidate.id, props.relationships.assets) ||
            props.allAssets[dropCandidate.id].publishedTo.length > 0
          ) {
            return false
          }
        }
        // Can't drop in same folder
        // Can't drop in UGC or Search folder
        if (
          utils.targetIsValidForAssets(props) &&
          validatePrivilege(
            'moveasset',
            props.privileges,
            props.email,
            props.groups,
            props.id,
            props.folders.byId,
          )
        ) {
          return true
        }
        return false
      }

      // Can't drop a folder on itself
      if (dropCandidate.id === props.id) {
        return false
      }

      // Can't delete a folder if it has referenced assets or folders
      if (dropCandidate.type === 'folder' && utils.targetIsRecycleBin(props)) {
        const allowed = validatePrivilege(
          'deletefolder',
          props.privileges,
          props.email,
          props.groups,
          dropCandidate.id,
          props.folders.byId,
        )
        const noReferences = !folderContainsReferencedAssets(
          dropCandidate.id,
          props.relationships.assets,
          props.folders.byId,
          props.endpoints.endpoints,
        )
        const noReferencedFolders = !folderContainsReferencedFolders(
          dropCandidate.id,
          props.folders.byId,
          props.relationships.folders,
        )
        return (
          allowed &&
          noReferences &&
          noReferencedFolders &&
          props.folders.byId[dropCandidate.id].parentId !== props.id &&
          dropCandidate.id !== props.id &&
          buildPath(dropCandidate.id, props.folders.byId).indexOf(
            props.folders.recycleBin,
          ) < 0
        )
      }

      /* Can't drop in parent folder, but can reorder to above/below parent */
      if (utils.targetIsValidForFolders(props)) {
        if (props.folders.byId[dropCandidate.id].parentId === props.id) {
          if (
            props.folderDragData &&
            props.folderDragData.over === null &&
            (props.folderDragData.above ===
              props.folders.byId[dropCandidate.id].parentId ||
              props.folderDragData.below ===
                props.folders.byId[dropCandidate.id].parentId)
          )
            return true
          return false
        } else {
          return (
            !isChild(props.id, dropCandidate.id, props.folders.byId) &&
            validatePrivilege(
              'movefolder',
              props.privileges,
              props.email,
              props.groups,
              props.id,
              props.folders.byId,
            ) &&
            validatePrivilege(
              'movefolder',
              props.privileges,
              props.email,
              props.groups,
              dropCandidate.id,
              props.folders.byId,
            )
          )
        }
      }
      return false
    }
    return false
  },

  hover: throttle((props, monitor, component) => {
    // Determine rectangle on screen
    const item = monitor.getItem()

    if (item.id === props.id) {
      if (
        props.folderDragData.over !== null ||
        props.folderDragData.above !== null ||
        props.folderDragData.below !== null
      )
        props.setFolderDragData(null, null, null)
      return
    }

    // If hover last 250ms, expand or collapse the folder
    if (!item.timeout && props.node.childFolders.length > 0) {
      item.timeout = setTimeout(() => {
        // Get mouse position and drop target position
        const mousePos = monitor.getClientOffset()
        const hoverTargetPosition = component
          .getDecoratedComponentInstance()
          .node.getBoundingClientRect()

        if (monitor.isOver()) {
          if (
            utils.shouldCloseOnHover(props, item, mousePos, hoverTargetPosition)
          )
            props.appActions.removeOpenFolderKey(props.id)
          else if (
            utils.shouldOpenOnHover(props, item, mousePos, hoverTargetPosition)
          )
            props.appActions.addOpenFolderKey(props.id)
        }

        item.timeout = null
      }, 250)
    }

    // Get mouse position and drop target position
    const mousePos = monitor.getClientOffset()
    const hoverTargetPosition = component
      .getDecoratedComponentInstance()
      .node.getBoundingClientRect()

    // When dragging assets or hovering root folders, we only care about "over"
    if (
      item.type === 'asset' ||
      (item.type === 'folder' && utils.folderDoesNotAllowReorder(props))
    ) {
      if (
        mousePos.y > hoverTargetPosition.top &&
        mousePos.y < hoverTargetPosition.bottom
      )
        if (
          props.folderDragData.over !== props.id ||
          props.folderDragData.above !== null ||
          props.folderDragData.below !== null
        )
          props.setFolderDragData(props.id, null, null)

      return
    }
    /*
     * Sibling positions must come from the DISPLAYED order, not from
     * childFolders: where the parent has an itemOrder the two differ, and the
     * "this drop is a no-op" tests below would then be judged against an order
     * the user cannot see - suppressing real drops and allowing pointless ones.
     * The comparison is only meaningful between siblings, hence sameParent.
     */
    const sameParent = utils.haveSameParent(
      props.folders.byId,
      item.id,
      props.id,
    )
    const dragIndex = utils.getDisplayIndex(props.folders.byId, item.id)
    const hoverIndex = utils.getDisplayIndex(props.folders.byId, props.id)
    // Dragged item is below hovered item (bottom 1/4)
    if (
      mousePos.y >
      hoverTargetPosition.top + (hoverTargetPosition.height * 3) / 4
    ) {
      // Already immediately after the hovered folder, so this drop would move
      // nothing. Clear the drag data rather than leaving it: anything left
      // over from a previously hovered folder would otherwise be applied on
      // drop, landing the folder somewhere the user never pointed at.
      if (
        (sameParent && dragIndex - hoverIndex === 1) ||
        props.id === props.folders.byId[item.id].parentId
      ) {
        if (
          props.folderDragData.over !== null ||
          props.folderDragData.above !== null ||
          props.folderDragData.below !== null
        )
          props.setFolderDragData(null, null, null)
      } else if (
        props.folderDragData.over !== null ||
        props.folderDragData.above !== null ||
        props.folderDragData.below !== props.id
      )
        props.setFolderDragData(null, null, props.id)
      // Dragged item is above hovered item (top 1/4)
    } else if (
      mousePos.y <
      hoverTargetPosition.bottom - (hoverTargetPosition.height * 3) / 4
    ) {
      // Already immediately before the hovered folder - same reasoning
      if (sameParent && hoverIndex - dragIndex === 1) {
        if (
          props.folderDragData.over !== null ||
          props.folderDragData.above !== null ||
          props.folderDragData.below !== null
        )
          props.setFolderDragData(null, null, null)
      } else if (
        props.folderDragData.over !== null ||
        props.folderDragData.above !== props.id ||
        props.folderDragData.below !== null
      )
        props.setFolderDragData(null, props.id, null)
      // Dragged item is over hovered item (middle 1/2)
    } else if (
      mousePos.y >
        hoverTargetPosition.bottom - (hoverTargetPosition.height * 3) / 4 &&
      mousePos.y <
        hoverTargetPosition.bottom - (hoverTargetPosition.height * 1) / 4
    ) {
      if (
        props.folderDragData.over !== props.id ||
        props.folderDragData.above !== null ||
        props.folderDragData.below !== null
      )
        props.setFolderDragData(props.id, null, null)
    } else {
      if (
        props.folderDragData.over !== null ||
        props.folderDragData.above !== null ||
        props.folderDragData.below !== null
      )
        props.setFolderDragData(null, null, null)
    }
  }, 50),
}

export const folderContainerTarget = {
  hover: throttle((props, monitor, component) => {
    const hoverTargetPosition =
      component.container && component.container.getBoundingClientRect()
    if (hoverTargetPosition) {
      const container = document.getElementById('sidebar-container')

      const mousePos = monitor.getClientOffset()
      if (mousePos.y < hoverTargetPosition.top + 42) {
        if (container.scrollTop > 0 && !container.scrolling) {
          scrollElement(
            container,
            container,
            container.scrollTop,
            -42,
            150,
            'vertical',
          )
        }
      }
      if (
        !container.scrolling &&
        mousePos.y > hoverTargetPosition.bottom - 42 &&
        container.scrollHeight > container.clientHeight &&
        container.scrollHeight - container.scrollTop > container.clientHeight
      ) {
        console.log(
          'scrollable down',
          container.clientHeight,
          container.scrollHeight,
          container.scrollTop,
        )
        scrollElement(
          container,
          container,
          container.scrollTop,
          42,
          150,
          'vertical',
        )
      }
    }
  }),
}

export const carouselItemSource = {
  beginDrag(props) {
    return { index: props.index }
  },
  canDrag() {
    return true
  },
  isDragging(props, monitor) {
    return monitor.getItem().index === props.index
  },
}

export const carouselItemTarget = {
  hover(props, monitor) {
    const item = monitor.getItem()
    if (item.index === props.index) {
      return
    }
    props.reorderCarousel(item.index, props.index)
    monitor.getItem().index = props.index
  },
}

// Menu item reorder drag/drop specs. The row is dragged by its handle only --
// the label input and the hide toggle inside it stay clickable.
export const menuItemSource = {
  beginDrag(props) {
    return { index: props.index }
  },
  isDragging(props, monitor) {
    return monitor.getItem().index === props.index
  },
}

export const menuItemTarget = {
  hover(props, monitor) {
    const item = monitor.getItem()
    if (item.index === props.index) {
      return
    }
    props.reorderMenuItem(item.index, props.index)
    monitor.getItem().index = props.index
  },
}

export const slideTarget = {
  hover(props, monitor, component) {
    // Determine rectangle on screen
    const item = monitor.getItem()
    if (item.index === props.index) {
      return
    }

    const mousePos = monitor.getClientOffset()
    const hoverTarget = component
      .getDecoratedComponentInstance()
      .slideRef.getBoundingClientRect()
    if (
      (props.index > item.index &&
        mousePos.x > hoverTarget.left + hoverTarget.width / 2) ||
      (props.index < item.index &&
        mousePos.x < hoverTarget.right - hoverTarget.width / 2)
    ) {
      props.reorderSlides(
        props.builderType,
        item.index,
        props.index,
        item.slide,
      )
      monitor.getItem().index = props.index
    }
  },
}

export const slideContainerTarget = {
  hover(props, monitor) {
    // Determine rectangle on screen
    const item = monitor.getItem()
    const container = document.getElementById('slide-container')
    const itemEl = document.getElementById(item.id)

    if (itemEl && container && !item.scrolling) {
      const itemPos = monitor.getSourceClientOffset()
      const containerRect = container.getBoundingClientRect()
      const itemRect = itemEl.getBoundingClientRect()
      if (itemPos.x < containerRect.x) {
        item.scrolling = true
        scrollElement(item, container, container.scrollLeft, -92, 500)
      }
      if (itemPos.x + itemRect.width > containerRect.right) {
        item.scrolling = true
        scrollElement(item, container, container.scrollLeft, 92, 500)
      }
    }
  },
}

export const slideSource = {
  beginDrag(props) {
    return {
      id: props.slide.id,
      slide: props.slide,
      index: props.index,
      builderType: props.builderType,
    }
  },
  canDrag() {
    return true
  },
  isDragging(props, monitor) {
    return monitor.getItem().id === props.slide.id
  },
}

export const uploadTarget = {
  drop(props, monitor) {
    const newFile = monitor.getItem().files[0]
    if (newFile.type !== '') {
      props.uploadDroppedFile(newFile)
      //props.assetActions.dropAsset(props.id, props.folders.activeFolder)
    } else {
      props.modalActions.setMessage({ type: 'ERROR', msg: 'Unknown File Type' })
      props.modalActions.openModal('MessageModal')
    }
  },

  canDrop() {
    return true
  },
}

export const hotspotTarget = {
  drop(props, monitor) {
    const item = monitor.getItem()
    const itemX = item.center.x + item.center.x * item.initialLocation.x
    const itemY = item.center.y - item.center.y * item.initialLocation.y
    const finalX = monitor.getClientOffset().x
    const finalY = monitor.getClientOffset().y
    const origX = monitor.getInitialClientOffset().x
    const origY = monitor.getInitialClientOffset().y
    const moveX = finalX - origX
    const moveY = origY - finalY
    const newX = itemX + moveX
    const newY = itemY - moveY
    const xPct = (newX - item.center.x) / item.center.x
    const yPct = (item.center.y - newY) / item.center.y
    props.builderActions.updateHotspot(
      item.builderType,
      item.slideId,
      item.id,
      {
        x: xPct,
        y: yPct,
      },
    )
  },
}

export const hotspotSource = {
  beginDrag(props) {
    return {
      id: props.hotspot.id,
      slideId: props.parentId,
      builderType: props.builderType,
      initialLocation: props.hotspot.location,
      center: props.center,
    }
  },
  canDrag() {
    return true
  },
  isDragging(props, monitor) {
    return monitor.getItem().id === props.hotspot.id
  },
}

export const navzoneSource = {
  beginDrag(props) {
    return {
      id: props.navzone.id,
      navzone: props.navzone,
      slideId: props.parentId,
      builderType: props.builderType,
      center: props.center,
    }
  },
  isDragging(props, monitor) {
    return monitor.getItem().id === props.navzone.id
  },
  canDrag(props) {
    return !props.dragHandleDown
  },
}

export const navzoneTarget = {
  drop(props, monitor) {
    const { builderType, builderActions, activeSlide } = props
    const item = monitor.getItem()
    const finalX = monitor.getClientOffset().x
    const finalY = monitor.getClientOffset().y
    const origX = monitor.getInitialClientOffset().x
    const origY = monitor.getInitialClientOffset().y
    const moveXPct = ((finalX - origX) / props.imageWidth) * 2
    const moveYPct = ((finalY - origY) / props.imageHeight) * 2
    const newLocation = {
      topLeft: {
        x: item.navzone.topLeft.x + moveXPct,
        y: item.navzone.topLeft.y - moveYPct,
      },
      bottomRight: {
        x: item.navzone.bottomRight.x + moveXPct,
        y: item.navzone.bottomRight.y - moveYPct,
      },
    }
    if (
      !isOutsideImage(newLocation) &&
      !isOverlapping(activeSlide.navzones, newLocation, item.navzone.id)
    ) {
      const newNavzone = {
        ...item.navzone,
        topLeft: newLocation.topLeft,
        bottomRight: newLocation.bottomRight,
      }
      builderActions.updateNavzone(builderType, activeSlide.id, newNavzone)
    }
  },
}

export function dragCollect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  }
}

export function dropCollect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  }
}

// Grid item reorder drag/drop specs
export const gridItemSource = {
  beginDrag(props) {
    return { id: props.gridItemId, type: 'folder' }
  },
  canDrag(props) {
    return !!props.canReorder
  },
}

// Also registered for DND_ASSET drags (assets keep their original drag
// source so they can still be dropped on sidebar folders), so gate on
// canReorder here rather than in the source. The pointer offset is passed
// up so TileView can resolve which zone (before/after/into) is hovered.
export const gridItemTarget = {
  canDrop(props) {
    return !!props.canReorder
  },
  hover(props, monitor) {
    if (!props.canReorder) return
    const item = monitor.getItem()
    if (item.id === props.gridItemId) return
    props.onHoverGridItem(item, props.gridItemId, monitor.getClientOffset())
  },
  drop(props, monitor) {
    if (!props.canReorder) return
    const item = monitor.getItem()
    if (item.id !== props.gridItemId) {
      props.onDropGridItem(item, props.gridItemId)
    }
  },
}

export function gridDragCollect(connect, monitor) {
  return {
    connectGridDragSource: connect.dragSource(),
    isGridDragging: monitor.isDragging(),
  }
}

export function gridDropCollect(connect, monitor) {
  return {
    connectGridDropTarget: connect.dropTarget(),
    isGridOver: monitor.isOver({ shallow: true }),
  }
}
